import { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Clapperboard, Handshake, WalletCards, Library,
  Calendar as CalendarIcon, LogOut, LockKeyhole, Menu, X, PenLine, ArrowUpRight, Users,
  Eye, EyeOff
} from "lucide-react";
import "@/App.css";
import {
  roleLabel, Dashboard, ScriptsView, ProductionView,
  CalendarView, BrandDealsView, FinancesView, ContentLibraryView, TeamView
} from "./views.jsx";
import { ErrorBoundary } from "./components/ErrorBoundary";

const RAW_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = (RAW_URL && !RAW_URL.includes("contenthq-backend")) ? RAW_URL : "http://localhost:8000";
const API = `${BASE_URL.replace(/\/$/, "")}/api`;

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "scripts", label: "Scripts", icon: PenLine },
  { id: "production", label: "Production", icon: Clapperboard },
  { id: "calendar", label: "Content Calendar", icon: CalendarIcon },
  { id: "deals", label: "Brand Deals", icon: Handshake },
  { id: "finances", label: "Finances", icon: WalletCards },
  { id: "library", label: "Content Library", icon: Library },
  { id: "team", label: "Team", icon: Users },
];

function Login({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState("admin");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/status`).then((res) => {
      if (res.data && !res.data.has_users) {
        setIsRegister(true);
      }
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isRegister) {
        const r = await axios.post(`${API}/auth/register`, { name, email, password, role });
        localStorage.setItem("hq_token", r.data.token);
        onLogin(r.data.user);
      } else {
        const r = await axios.post(`${API}/auth/login`, { email, password });
        localStorage.setItem("hq_token", r.data.token);
        onLogin(r.data.user);
      }
    } catch (x) {
      setError(x.response?.data?.detail || (isRegister ? "Unable to register account" : "Unable to sign in"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-shell">
      <div className="login-art">
        <div className="brand-mark">CH</div>
        <p className="eyebrow">CONTENT HUB / CREATOR OS</p>
        <h1>Make the work<br/><em>move.</em></h1>
        <p className="art-copy">One focused workspace for the ideas, scripts, production pipeline, and brand partnerships.</p>
        <div className="signal"><span/>Connected to Live Database</div>
      </div>
      <form className="login-form" onSubmit={submit}>
        <div className="mobile-brand"><div className="brand-mark">CH</div><b>Content Hub</b></div>
        <p className="eyebrow">{isRegister ? "FIRST-TIME SETUP / SIGN UP" : "WELCOME BACK"}</p>
        <h2>{isRegister ? "Create your account" : "Sign in to Content Hub"}</h2>
        <p className="muted">{isRegister ? "Set up your admin or team account to get started." : "Enter your credentials to continue."}</p>
        
        {isRegister && (
          <label>Full Name
            <input data-testid="register-name-input" value={name} onChange={e=>setName(e.target.value)} type="text" placeholder="John Doe" required />
          </label>
        )}

        <label>Email
          <input data-testid="login-email-input" value={email} onChange={e=>setEmail(e.target.value)} type="email" placeholder="you@company.com" required />
        </label>
        <label>Password
          <div className="password-input-wrap">
            <input
              data-testid="login-password-input"
              value={password}
              onChange={e=>setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              placeholder="••••••••"
              required
            />
            <button
              type="button"
              className="password-toggle-btn"
              data-testid="login-password-toggle-button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </label>

        {isRegister && (
          <label>Role
            <select data-testid="register-role-select" value={role} onChange={e=>setRole(e.target.value)} style={{ padding: "0.6rem", borderRadius: "8px", border: "1px solid #334155", background: "#0f172a", color: "#f8fafc", marginTop: "4px" }}>
              <option value="admin">Admin / Owner</option>
              <option value="manager">Manager</option>
              <option value="script_writer">Script Writer</option>
              <option value="editor">Editor</option>
            </select>
          </label>
        )}

        {error && <div className="error" data-testid="login-error">{error}</div>}
        <button className="primary-btn" data-testid="login-submit-button" disabled={loading}>
          {loading ? "Processing..." : (isRegister ? "Create Account & Enter" : "Enter workspace")} <ArrowUpRight size={17}/>
        </button>

        <div style={{ marginTop: "1rem", textAlign: "center" }}>
          <button
            type="button"
            style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: "0.85rem", textDecoration: "underline" }}
            onClick={() => { setIsRegister(!isRegister); setError(""); }}
          >
            {isRegister ? "Already have an account? Sign in" : "Need a new account? Register"}
          </button>
        </div>

        <p className="login-note"><LockKeyhole size={14}/> Private workspace · Live Database</p>
      </form>
    </main>
  );
}

function Sidebar({ user, active, go, mobile, setMobile, onLogout }) {
  const allowedNav = NAV.filter((n) => user.role === "admin" || (user.permissions || []).includes(n.id));

  return (
    <aside className={mobile ? "sidebar open" : "sidebar"}>
      <div className="side-top">
        <div className="brand-row"><div className="brand-mark small">CH</div><span>Content <b>Hub</b></span></div>
        <button className="icon-btn mobile-close" data-testid="close-navigation-button" onClick={()=>setMobile(false)}><X size={18}/></button>
      </div>
      <div className="workspace"><span className="avatar mini">{user.name[0]}</span><div><b>Creator Studio</b><small>{user.role === "admin" ? "Admin Workspace" : (roleLabel[user.role] || "Team Member")}</small></div><span className="dot"/></div>
      <nav>
        {allowedNav.map((n) => {
          const I = n.icon;
          const isActive = active === n.id;
          return (
            <button key={n.id} data-testid={`navigation-${n.id}-button`} className={isActive ? "nav-item active" : "nav-item"} onClick={()=>go(n.id)}>
              <I size={17}/>{n.label}
            </button>
          );
        })}
      </nav>
      <div className="side-bottom">
        <div className="profile" data-testid="profile-menu">
          <span className="avatar">{user.name[0]}</span>
          <span><b>{user.name}</b><small>{roleLabel[user.role] || "Creator"}</small></span>
          <button className="logout" data-testid="logout-button" onClick={onLogout} aria-label="Log out"><LogOut size={16}/></button>
        </div>
      </div>
    </aside>
  );
}

function Topbar({ active, openMobile }) {
  const label = NAV.find((n)=>n.id===active)?.label || "Dashboard";
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  return (
    <header className="topbar">
      <button className="icon-btn menu-btn" data-testid="open-navigation-button" onClick={openMobile}><Menu size={19}/></button>
      <div className="crumb">Content Hub <span>/</span> <b>{label}</b></div>
      <div className="top-actions">
        <span className="status-live"><i/>All systems live</span>
        <span className="top-date">{today}</span>
      </div>
    </header>
  );
}

function AppShell({ user, onLogout }) {
  const [mobile, setMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const seg = location.pathname.split("/").filter(Boolean);

  const hasPerm = (tabId) => user.role === "admin" || (user.permissions || []).includes(tabId);
  const allowedNav = NAV.filter((n) => hasPerm(n.id));
  const defaultTab = hasPerm("dashboard") ? "dashboard" : (allowedNav[0]?.id || "production");
  const active = seg[0] || defaultTab;
  const go = (id) => { navigate(`/${id}`); setMobile(false); };
  const token = localStorage.getItem("hq_token");

  return (
    <div className="app-shell">
      <Sidebar user={user} active={active} go={go} mobile={mobile} setMobile={setMobile} onLogout={onLogout}/>
      <section className="main">
        <Topbar active={active} openMobile={()=>setMobile(true)}/>
        <div className="page-wrap">
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/dashboard" element={hasPerm("dashboard") ? <Dashboard user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/scripts" element={hasPerm("scripts") ? <ScriptsView user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/production" element={hasPerm("production") ? <ProductionView user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/calendar" element={hasPerm("calendar") ? <CalendarView user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/deals" element={hasPerm("deals") ? <BrandDealsView user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/finances" element={hasPerm("finances") ? <FinancesView user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/library" element={hasPerm("library") ? <ContentLibraryView user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              <Route path="/team" element={hasPerm("team") ? <TeamView user={user} token={token}/> : <Navigate to={`/${defaultTab}`} replace />}/>
              
              {/* Legacy redirect mappings */}
              <Route path="/content" element={<Navigate to="/production" replace />}/>
              <Route path="/finance" element={<Navigate to="/finances" replace />}/>
              <Route path="*" element={<Navigate to={`/${defaultTab}`} replace />}/>
            </Routes>
          </ErrorBoundary>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    const token = localStorage.getItem("hq_token");
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setUser(r.data))
        .catch(() => localStorage.removeItem("hq_token"))
        .finally(() => setChecking(false));
    } else setChecking(false);
  }, []);

  if (checking) return <div className="loading-screen">Loading your workspace…</div>;
  return (
    <BrowserRouter>
      {user ? <AppShell user={user} onLogout={() => { localStorage.removeItem("hq_token"); setUser(null); }}/>
            : <Routes><Route path="*" element={<Login onLogin={setUser}/>}/></Routes>}
    </BrowserRouter>
  );
}
